import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import {
  adminAuthSendCode,
  adminAuthLogin,
  OpenAPI,
  AdminSendCodeDto,
  ApiError,
} from '@pro-sto/api-client';
import type { AdminUserDto, AuthRefreshResponse, AdminAuthResponseDto } from '@pro-sto/api-client';
import { toastStore } from './ToastStore';
import { notificationService } from '@/services/notificationService';

export class AuthOperationInProgressError extends Error {
  constructor(message = 'Операция авторизации уже выполняется') {
    super(message);
    this.name = 'AuthOperationInProgressError';
  }
}

const getAuthErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError) {
    if (error.body && typeof error.body === 'object' && 'message' in error.body) {
      const message = (error.body as { message?: string }).message;
      if (typeof message === 'string' && message.length > 0) {
        return message;
      }
    }
  }

  return fallback;
};

/**
 * Store для управления аутентификацией администраторов
 * Использует adminAuthSendCode и adminAuthLogin для SMS-авторизации
 */
export class AuthStore {
  isAuthenticated = false;
  accessToken: string | null = null;
  refreshToken: string | null = null;
  user: AdminUserDto | null = null;
  isLoading = false;
  private isVerifying = false;

  constructor() {
    makeAutoObservable(this);
    this.checkAuth();
  }

  /**
   * Проверка наличия токенов при загрузке приложения
   * Восстанавливает состояние из localStorage
   */
  checkAuth = () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const userStr = localStorage.getItem('user');

    if (accessToken && refreshToken && userStr) {
      try {
        const user = JSON.parse(userStr) as AdminUserDto;

        if (
          user.auth_panel === AdminSendCodeDto.panel.SUPERADMIN ||
          (user.auth_panel === undefined && !user.service_center_uuid)
        ) {
          this.logout();
          return;
        }

        runInAction(() => {
          this.accessToken = accessToken;
          this.refreshToken = refreshToken;
          this.user = user;
          this.isAuthenticated = true;
        });

        // Устанавливаем токен в OpenAPI для всех запросов
        OpenAPI.TOKEN = accessToken;

        // Инициализируем уведомления после восстановления сессии
        notificationService.initialize().catch((error) => {
          console.error('Failed to initialize notifications after auth restore:', error);
        });
      } catch (error) {
        console.error('Ошибка при парсинге данных пользователя:', error);
        this.logout();
      }
    }
  };

  /**
   * Сохранение сессии после login / impersonation exchange
   */
  setSession = (response: AdminAuthResponseDto) => {
    runInAction(() => {
      this.accessToken = response.accessToken;
      this.refreshToken = response.refreshToken;
      this.user = response.user;
      this.isAuthenticated = true;
      this.isLoading = false;
    });

    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('user', JSON.stringify(response.user));

    OpenAPI.TOKEN = response.accessToken;

    notificationService.initialize().catch((error) => {
      console.error('Failed to initialize notifications after login:', error);
    });
  };

  /**
   * Отправка SMS кода на номер телефона администратора
   */
  sendCode = async (phone: string): Promise<void> => {
    this.isLoading = true;

    try {
      await adminAuthSendCode({
        requestBody: {
          phone,
          panel: AdminSendCodeDto.panel.SERVICE_CENTER,
        },
      });
      
      // Успех - код отправлен
      runInAction(() => {
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
      });
      
      toastStore.showError(
        getAuthErrorMessage(error, 'Не удалось отправить код. Проверьте номер телефона.'),
      );
      throw error;
    }
  };

  /**
   * Вход по SMS коду
   */
  verifyCode = async (phone: string, code: string): Promise<void> => {
    if (this.isVerifying) {
      throw new AuthOperationInProgressError();
    }

    this.isVerifying = true;
    this.isLoading = true;

    try {
      const response = await adminAuthLogin({
        requestBody: {
          phone,
          code,
          panel: AdminSendCodeDto.panel.SERVICE_CENTER,
        },
      });

      this.setSession(response);
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
      });

      if (!(error instanceof AuthOperationInProgressError)) {
        toastStore.showError(
          getAuthErrorMessage(error, 'Неверный код или истек срок действия.'),
        );
      }

      throw error;
    } finally {
      runInAction(() => {
        this.isVerifying = false;
      });
    }
  };

  /**
   * Обновление токенов
   * Используется interceptor'ом при получении 401 ошибки
   */
  refreshTokens = async (): Promise<void> => {
    if (!this.refreshToken) {
      console.error('Refresh token отсутствует');
      this.logout();
      return;
    }

    try {
      // Используем прямой axios запрос с явной передачей refresh token
      // Это избегает race condition с глобальным OpenAPI.TOKEN
      const response = await axios.post<AuthRefreshResponse>(
        `${OpenAPI.BASE}/api/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.refreshToken}`,
          },
        }
      );

      runInAction(() => {
        this.accessToken = response.data.accessToken;
        localStorage.setItem('accessToken', response.data.accessToken);
        this.refreshToken = response.data.refreshToken;
        localStorage.setItem('refreshToken', response.data.refreshToken);
      });

      // Обновляем токен в OpenAPI на новый access token
      OpenAPI.TOKEN = response.data.accessToken;
    } catch (error) {
      console.error('Ошибка при обновлении токенов:', error);
      
      // Разлогиниваем только при 401 ошибке (невалидный/истекший refresh token)
      // При network ошибках не разлогиниваем пользователя
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        this.logout();
      }
      
      throw error; // Пробрасываем ошибку для interceptor
    }
  };

  /**
   * Выход из системы
   * Очищает все данные аутентификации
   */
  logout = () => {
    runInAction(() => {
      this.isAuthenticated = false;
      this.accessToken = null;
      this.refreshToken = null;
      this.user = null;
    });

    // Очищаем токен в OpenAPI
    OpenAPI.TOKEN = undefined;

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  };
}

// Экспортируем singleton instance
export const authStore = new AuthStore();
